/**
 * Page through a Supabase/PostgREST query until every row is fetched.
 *
 * An unbounded `select()` is silently capped at PostgREST's default 1000-row
 * limit. For any query expected to return the *full* set of a table that can
 * exceed 1000 rows, that cap silently drops rows. This helper repeatedly calls
 * the query with `.range()` windows until a short page signals the end.
 *
 * The `page` callback must build a *fresh* query each call (a PostgREST builder
 * is single-use) and apply the given range. For deterministic page boundaries,
 * order by a unique (or unique-tiebroken) column — otherwise rows can be
 * skipped or duplicated across pages.
 *
 * @example
 * const rows = await fetchAllRows<Row>((from, to) =>
 *   supabase.from("t").select("*").order("id").range(from, to)
 * );
 */
export const FETCH_ALL_PAGE_SIZE = 1000;

export async function fetchAllRows<T>(
  page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += FETCH_ALL_PAGE_SIZE) {
    const { data, error } = await page(from, from + FETCH_ALL_PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < FETCH_ALL_PAGE_SIZE) break;
  }
  return all;
}
